import confetti from "assets/js/confetti.js";

/**
 * @typedef {Object} ConfettiOptions
 * @property {number} [particleCount]
 * @property {number} [angle]
 * @property {number} [spread]
 * @property {number} [startVelocity]
 * @property {number} [decay]
 * @property {number} [gravity]
 * @property {number} [ticks]
 * @property {{x: number, y: number}} [origin]
 * @property {string[]} [colors]
 * @property {string[]} [shapes]
 * @property {number} [scalar]
 * @property {number} [drift]
 * @property {boolean} [disableForReducedMotion]
 * @property {number} [zIndex]
 * @property {boolean} [intense] - When true, fires a more abundant, widespread
 *   burst with two staggered side-bursts for full-screen coverage. Use for
 *   major milestones (e.g. 100% completion). Defaults to `false`.
 */

/**
 * Triggers confetti from the position of a target element.
 *
 * @param {string} target - CSS selector for the element to launch confetti from.
 * @param {ConfettiOptions} [options={}] - Custom confetti options.
 * @returns {ConfettiOptions}
 */
export const useConfetti = (target = null, options = {}) => {
  if (!target) {
    throw new Error("Target is not set");
  }

  const targetEl = document.querySelector(target);
  if (!targetEl) {
    throw new Error("Target element not found");
  }

  const rect = targetEl.getBoundingClientRect();

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  const { intense = false, ...userOptions } = options;

  const defaultOptions = intense
    ? {
        zIndex: 9999,
        particleCount: 200,
        spread: 160,
        startVelocity: 55,
        gravity: 0.8,
        ticks: 300,
        origin: {
          x: centerX / window.innerWidth,
          y: centerY / window.innerHeight,
        },
      }
    : {
        zIndex: 9999,
        particleCount: 100,
        spread: 70,
        origin: {
          x: centerX / window.innerWidth,
          y: centerY / window.innerHeight,
        },
      };

  const finalOptions = {
    ...defaultOptions,
    ...userOptions,
    origin: {
      ...defaultOptions.origin,
      ...(userOptions.origin || {}),
    },
  };

  confetti(finalOptions);

  // Two staggered side-bursts so the coverage feels truly full-screen.
  if (intense) {
    const sideBurst = (originX) => ({
      ...finalOptions,
      particleCount: 120,
      spread: 110,
      startVelocity: 45,
      origin: { x: originX, y: 0.4 },
    });
    setTimeout(() => confetti(sideBurst(0.2)), 150);
    setTimeout(() => confetti(sideBurst(0.8)), 300);
  }

  return finalOptions;
};
